// mui
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Link,
  Stack,
  Typography,
} from '@mui/material';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import {
  ActionAttachments,
  ActionAttachment,
} from '@/pwa/molecules/ActionAttachments/ActionAttachments';

export interface ActionDetailProps {
  /** Meta lines (full display strings), each rendered only when provided. */
  regardingLabel?: string;
  createdByLabel?: string;
  assigneeLabel?: string;
  dueLabel?: string;
  recurrenceLabel?: string;
  /** Free-text note/description shown in a card. */
  note?: string;
  /** Linked call ("from your call"), shown only when present. */
  linkedCall?: { detail: string; fromName?: string };
  onLinkedCallClick?: () => void;
  /** Generic deep-link CTA (e.g. "Upload document"). */
  cta?: { label: string; onClick: () => void };
  attachments?: ActionAttachment[];
  onOpenAttachment?: (id: string) => void;
  /** When set, the action is done: a completion banner shows above the body. */
  done?: { dateLabel: string; onMarkAsTodo: () => void };
}

/**
 * ActionDetail is the carer-facing read-only action body: an optional completion
 * banner, meta lines, an optional note, the linked call, and an uploads section
 * with a generic deep-link CTA. Presentational only and dialog-free — the
 * surrounding dialog owns the title and the footer actions (see ActionEditor +
 * the wiring view).
 */
export const ActionDetail = ({
  regardingLabel,
  createdByLabel,
  assigneeLabel,
  dueLabel,
  recurrenceLabel,
  note,
  linkedCall,
  onLinkedCallClick,
  cta,
  attachments = [],
  onOpenAttachment,
  done,
}: ActionDetailProps) => {
  const metaLines = [
    regardingLabel,
    createdByLabel,
    assigneeLabel,
    dueLabel,
    recurrenceLabel,
  ].filter((line): line is string => Boolean(line));

  const ctaButton = cta && (
    <Button
      variant='outlined'
      color='inherit'
      size='large'
      fullWidth
      className='dashed'
      onClick={cta.onClick}
    >
      {cta.label}
    </Button>
  );

  const showAttachments = attachments.length > 0 || (!!cta && !done);

  return (
    <Stack spacing={3}>
      {done && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 3,
            py: 2,
            borderRadius: 2,
            bgcolor: 'success.main',
            color: 'success.contrastText',
          }}
        >
          <Icon icon='check-circle' fontSize={20} />
          <Typography
            variant='body1'
            sx={{
              color: 'inherit',
            }}
          >
            Marked as done on {done.dateLabel}.{' '}
            <Link
              component='button'
              type='button'
              onClick={done.onMarkAsTodo}
              color='inherit'
              underline='always'
            >
              Mark as todo?
            </Link>
          </Typography>
        </Box>
      )}
      {metaLines.length > 0 && (
        <Stack spacing={1}>
          {metaLines.map((line) => (
            <Typography
              key={line}
              variant='body1'
              sx={{
                color: 'text.secondary',
              }}
            >
              {line}
            </Typography>
          ))}
        </Stack>
      )}
      {note && (
        <Card>
          <CardContent>
            <Typography
              variant='body1'
              sx={{
                color: 'text.secondary',
              }}
            >
              {note}
            </Typography>
          </CardContent>
        </Card>
      )}
      {linkedCall && (
        <Card>
          <CardActionArea onClick={onLinkedCallClick}>
            <CardContent>
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Stack
                  sx={{
                    gap: 1,
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      color: 'text.primary',
                    }}
                  >
                    Linked call
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {linkedCall.detail}
                  </Typography>
                  {linkedCall.fromName && (
                    <Typography
                      variant='body1'
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      From {linkedCall.fromName}
                    </Typography>
                  )}
                </Stack>
                <Box
                  sx={{
                    display: 'flex',
                    color: 'text.secondary',
                  }}
                >
                  <Icon icon='chevron-right' fontSize={20} />
                </Box>
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      )}
      {showAttachments && (
        <ActionAttachments
          attachments={attachments}
          onOpenAttachment={onOpenAttachment}
          uploadSlot={done ? undefined : ctaButton}
        />
      )}
    </Stack>
  );
};

ActionDetail.displayName = 'ActionDetail';
export default ActionDetail;
